import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import quentariLogo from "@/assets/quentari-logo.svg";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { Menu, ArrowRight } from "lucide-react";
import { MobileMenu } from "./MobileMenu";

const navItems = [
  { label: "Prozess", id: "process" },
  { label: "Leistungen", id: "features" },
  { label: "Ergebnisse", id: "outcomes" },
  { label: "FAQ", id: "faq" },
];

export const LandingNavbar = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Trigger entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Track scroll position and active section
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    setScrollY(currentScrollY);

    // Find active section based on scroll position
    const sections = navItems.map(item => ({
      id: item.id,
      element: document.getElementById(item.id)
    })).filter(s => s.element);

    const viewportMiddle = currentScrollY + window.innerHeight / 3;
    
    let current: string | null = null;
    for (const section of sections) {
      if (section.element) {
        const rect = section.element.getBoundingClientRect();
        const sectionTop = rect.top + currentScrollY;
        const sectionBottom = sectionTop + rect.height;
        
        if (viewportMiddle >= sectionTop && viewportMiddle < sectionBottom) {
          current = section.id;
          break;
        }
      }
    }
    
    setActiveSection(current);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Calculate dynamic styles based on scroll
  const scrollProgress = Math.min(scrollY / 120, 1);
  const isScrolled = scrollY > 20;

  return (
    <>
      <nav
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-out"
        )}
        style={{
          backgroundColor: isScrolled 
            ? `hsl(var(--background) / ${0.75 + scrollProgress * 0.2})` 
            : 'transparent',
          backdropFilter: isScrolled ? `blur(${12 + scrollProgress * 12}px) saturate(${1.2 + scrollProgress * 0.3})` : 'none',
          WebkitBackdropFilter: isScrolled ? `blur(${12 + scrollProgress * 12}px) saturate(${1.2 + scrollProgress * 0.3})` : 'none',
          boxShadow: isScrolled 
            ? `0 1px 2px hsl(var(--foreground) / ${scrollProgress * 0.03}), 
               0 4px 16px hsl(var(--foreground) / ${scrollProgress * 0.04}),
               0 0 40px hsl(var(--primary) / ${scrollProgress * 0.03})` 
            : 'none',
          borderBottom: isScrolled 
            ? `1px solid hsl(var(--border) / ${scrollProgress * 0.4})` 
            : '1px solid transparent',
        }}
      >
        {/* Subtle top accent line */}
        <div 
          className="absolute top-0 left-0 right-0 h-px transition-opacity duration-500"
          style={{
            opacity: isScrolled ? scrollProgress * 0.5 : 0,
            background: `linear-gradient(90deg, transparent 0%, hsl(var(--primary) / 0.3) 50%, transparent 100%)`,
          }}
        />

        <div className="container max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex h-16 md:h-20 items-center justify-between">
            {/* Logo with premium hover effect */}
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className={cn(
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg group relative transition-all duration-700",
                isLoaded ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
              )}
              aria-label="Zurück nach oben scrollen"
            >
              {/* Logo glow on hover */}
              <span 
                className="absolute -inset-3 rounded-xl bg-primary/0 group-hover:bg-primary/5 blur-xl transition-all duration-500 pointer-events-none"
                aria-hidden="true"
              />
              <img 
                src={quentariLogo} 
                alt="Quentari" 
                width={140}
                height={35}
                className="h-7 md:h-8 lg:h-9 w-auto transition-all duration-300 group-hover:scale-[1.02] relative z-10" 
              />
            </button>
            
            {/* Desktop Navigation - Premium styling */}
            <div 
              className={cn(
                "hidden md:flex items-center gap-0.5 transition-all duration-700",
                isLoaded ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
              )}
              style={{ transitionDelay: '100ms' }}
            >
              {navItems.map((item, index) => {
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={cn(
                      "relative px-4 lg:px-5 py-2.5 text-sm font-medium transition-all duration-300 rounded-lg group"
                    )}
                    style={{ 
                      transitionDelay: `${(index + 1) * 50}ms`,
                    }}
                  >
                    {/* Background highlight with gradient */}
                    <span 
                      className={cn(
                        "absolute inset-0 rounded-lg transition-all duration-300",
                        isActive 
                          ? "bg-gradient-to-b from-primary/10 to-primary/5" 
                          : "bg-transparent group-hover:bg-muted/40"
                      )}
                    />
                    
                    {/* Label with color transition */}
                    <span 
                      className={cn(
                        "relative z-10 transition-colors duration-300",
                        isActive 
                          ? "text-primary" 
                          : "text-foreground/60 group-hover:text-foreground/90"
                      )}
                    >
                      {item.label}
                    </span>
                    
                    {/* Premium underline indicator */}
                    <span 
                      className={cn(
                        "absolute bottom-1 left-1/2 h-[2px] rounded-full bg-primary transition-all duration-400 ease-out",
                        isActive 
                          ? "w-6 -translate-x-1/2 opacity-100" 
                          : "w-0 -translate-x-1/2 opacity-0 group-hover:w-4 group-hover:opacity-50"
                      )}
                    />
                    
                    {/* Glow effect for active */}
                    {isActive && (
                      <span 
                        className="absolute bottom-1 left-1/2 -translate-x-1/2 w-6 h-[2px] bg-primary/50 blur-sm pointer-events-none"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* CTA Buttons - Premium styling */}
            <div 
              className={cn(
                "flex items-center gap-2 lg:gap-3 transition-all duration-700",
                isLoaded ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
              )}
              style={{ transitionDelay: '200ms' }}
            >
              {user ? (
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="h-10 px-5 lg:px-6 font-medium hidden lg:inline-flex transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 hover:-translate-y-0.5 group"
                >
                  <span>Zum Dashboard</span>
                  <ArrowRight className="ml-1.5 w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/auth')}
                    className="h-10 px-4 lg:px-5 font-medium hidden lg:inline-flex text-foreground/70 hover:text-foreground hover:bg-muted/40 transition-all duration-300"
                  >
                    Anmelden
                  </Button>
                  <Button
                    onClick={() => window.location.href = '/auth'}
                    className="h-10 px-5 lg:px-7 font-medium hidden lg:inline-flex transition-all duration-300 hover:shadow-xl hover:shadow-primary/25 hover:-translate-y-0.5 group relative overflow-hidden"
                  >
                    {/* Shimmer effect on hover */}
                    <span 
                      className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-primary-foreground/10 to-transparent pointer-events-none"
                      aria-hidden="true"
                    />
                    <span className="relative z-10">Erste Entscheidung strukturieren</span>
                    <ArrowRight className="ml-1.5 w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 relative z-10" />
                  </Button>
                </>
              )}

              {/* Mobile Menu Button - Enhanced */}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="lg:hidden p-2.5 rounded-xl hover:bg-muted/40 transition-all duration-300 active:scale-95 group relative"
                aria-label="Menü öffnen"
              >
                {/* Glow on hover */}
                <span 
                  className="absolute -inset-1 rounded-xl bg-primary/0 group-hover:bg-primary/5 blur-lg transition-all duration-300 pointer-events-none"
                  aria-hidden="true"
                />
                <Menu className="w-5 h-5 text-foreground/80 group-hover:text-foreground transition-colors duration-300 relative z-10" />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom glow accent when scrolled */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none transition-opacity duration-500"
          style={{
            opacity: isScrolled ? scrollProgress * 0.3 : 0,
            background: `linear-gradient(180deg, transparent 0%, hsl(var(--primary) / 0.02) 100%)`,
          }}
        />
      </nav>

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onNavigate={scrollToSection}
        user={user}
        onAuthClick={() => navigate('/auth')}
        onDashboardClick={() => navigate('/dashboard')}
      />
    </>
  );
};
